import clsx from 'clsx';
import type { ComponentPropsWithoutRef, ElementType, ReactElement, ReactNode } from 'react';

export type ButtonColor = 'default' | 'subtle' | 'danger' | 'raised' | 'muted';
export type ButtonVariant = 'solid' | 'outline' | 'ghost' | 'link' | 'round';
type LegacyButtonVariant = 'default' | 'destructive' | 'ghost' | 'link' | 'raised' | 'round' | 'secondary' | 'subtle';

type ButtonVariantValue = ButtonVariant | LegacyButtonVariant;
type ColorableButtonVariant = 'solid' | 'round';

type ButtonSharedProps<E extends ElementType = 'button'> = {
    readonly as?: E;
    readonly children: ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly disabledVariant?: 'dark' | 'light';
    readonly iconOnly?: boolean;
    readonly isActive?: boolean;
    readonly size?: 'm' | 'sm' | 'xs';
    readonly type?: 'button' | 'reset' | 'submit';
};

type AllowedButtonColor<V extends ButtonVariantValue | undefined> = V extends ColorableButtonVariant | undefined
    ? ButtonColor
    : never;

type ButtonOwnProps<
    E extends ElementType = 'button',
    V extends ButtonVariantValue | undefined = ButtonVariantValue | undefined,
> = ButtonSharedProps<E> & {
    readonly color?: AllowedButtonColor<V>;
    // Supports modern values plus legacy values for gradual migration.
    readonly variant?: V;
};

export type ButtonProps<
    E extends ElementType = 'button',
    V extends ButtonVariantValue | undefined = ButtonVariantValue | undefined,
> = ButtonOwnProps<E, V> & Omit<ComponentPropsWithoutRef<E>, keyof ButtonOwnProps<E>>;

/* ============================================
 * Size classes: padding + typography
 * ============================================ */
const getSizeClasses = (size: ButtonProps['size'], iconOnly: boolean): string => {
    switch (size) {
        case 'xs':
            return clsx('text-xxs font-semibold leading-[1rem]', iconOnly ? 'p-xs' : 'py-xs px-s');
        case 'sm':
            return clsx(
                'text-sm leading-[1.25rem]',
                iconOnly ? 'p-[calc(var(--spacing-m)+var(--spacing-xxs))]' : 'py-m px-xl',
            );
        case 'm':
            return clsx(
                'text-sm leading-[1.25rem]',
                iconOnly ? 'p-[calc(var(--spacing-l)+var(--spacing-xxs))]' : 'py-l px-xl',
            );
        default:
            return clsx(
                'text-sm leading-[1.25rem]',
                iconOnly ? 'p-[calc(var(--spacing-m)+var(--spacing-xxs))]' : 'py-m px-xxl',
            );
    }
};

/* ============================================
 * Round variant: static class maps (must be scannable by Tailwind JIT)
 * ============================================ */
const roundIconOnlyClasses: Record<string, string> = {
    m: 'rounded-full! h-[var(--spacing-8xl)] w-[var(--spacing-8xl)] p-0 [&>svg]:h-[var(--spacing-xxl)] [&>svg]:w-[var(--spacing-xxl)]',
    sm: 'rounded-full! h-[var(--spacing-5xl)] w-[var(--spacing-5xl)] p-0 [&>svg]:h-[var(--spacing-xl)] [&>svg]:w-[var(--spacing-xl)]',
    xs: 'rounded-full! h-[var(--spacing-4xl)] w-[var(--spacing-4xl)] p-0 [&>svg]:h-[var(--spacing-l)] [&>svg]:w-[var(--spacing-l)]',
};

/* ============================================
 * Variant helpers (extracted to reduce complexity)
 * ============================================ */
const getDefaultVariantClasses = (disabled: boolean, disabledVariant: ButtonProps['disabledVariant']): string => {
    const disabledBg = disabledVariant === 'dark' ? 'bg-black-900' : 'bg-gray-500';

    return clsx(
        'text-white-200 transition-none',
        disabled ? disabledBg : 'bg-black-900',
        !disabled && 'hover:bg-black-800',
    );
};

const getRaisedColorClasses = (disabled: boolean): string =>
    disabled
        ? 'bg-gray-200 shadow-none text-gray-800'
        : 'bg-gray-100 shadow-sm text-gray-800 hover:bg-gray-200 hover:shadow-md';

const getMutedColorClasses = (disabled: boolean): string =>
    disabled ? 'bg-gray-500 text-white-200' : 'bg-gray-500 text-white-200 hover:bg-black-900';

const getSolidVariantClasses = (
    color: ButtonColor,
    disabled: boolean,
    disabledVariant: ButtonProps['disabledVariant'],
    isActive: boolean,
): string => {
    if (color === 'danger') {
        return 'bg-error-600 text-white-200';
    }

    if (color === 'subtle') {
        return clsx(
            isActive
                ? 'bg-orange-100 text-orange-800 hover:bg-orange-100 hover:text-orange-800'
                : clsx('bg-gray-100 text-gray-900 shadow-sm', !disabled && 'hover:bg-gray-300'),
        );
    }

    if (color === 'raised') {
        return clsx('rounded-xs', getRaisedColorClasses(disabled));
    }

    if (color === 'muted') {
        return getMutedColorClasses(disabled);
    }

    return getDefaultVariantClasses(disabled, disabledVariant);
};

const getOutlineVariantClasses = (disabled: boolean, isActive: boolean): string =>
    clsx(
        'bg-white-200 border border-gray-400',
        isActive ? 'text-orange-800' : 'text-gray-900',
        !disabled && 'hover:bg-gray-100 hover:border-gray-500',
    );

const getGhostVariantClasses = (disabled: boolean, isActive: boolean): string =>
    clsx(
        'bg-transparent transition-none',
        isActive ? 'text-orange-800' : 'text-gray-900',
        !disabled && 'hover:bg-gray-100',
    );

const getRoundVariantClasses = (
    color: ButtonColor,
    disabled: boolean,
    iconOnly: boolean,
    size: ButtonProps['size'],
): string => {
    const sizeKey = size ?? 'sm';
    const colorClasses =
        color === 'danger'
            ? 'bg-error-600 text-white-200 hover:bg-error-700'
            : color === 'muted'
              ? getMutedColorClasses(disabled)
              : color === 'raised'
                ? getRaisedColorClasses(disabled)
                : color === 'subtle'
                  ? disabled
                      ? 'bg-gray-200 text-gray-800'
                      : 'bg-gray-100 text-gray-900 shadow-sm hover:bg-gray-300'
                  : disabled
                    ? 'bg-gray-400 text-white-200'
                    : 'bg-black-900 text-white-200 hover:bg-black-800';

    return clsx(colorClasses, iconOnly ? roundIconOnlyClasses[sizeKey] : 'rounded-full');
};

const getLinkVariantClasses = (disabled: boolean): string =>
    clsx(
        'bg-transparent no-underline',
        disabled ? 'text-gray-500' : 'text-black-900 hover:text-black-800 hover:no-underline',
        'py-xs px-s',
    );

/* ============================================
 * Variant classes
 * ============================================ */
const LEGACY_VARIANT_TO_PRESENTATION: Record<LegacyButtonVariant, { color: ButtonColor; variant: ButtonVariant }> = {
    default: { color: 'default', variant: 'solid' },
    destructive: { color: 'danger', variant: 'solid' },
    ghost: { color: 'default', variant: 'ghost' },
    link: { color: 'default', variant: 'link' },
    raised: { color: 'raised', variant: 'solid' },
    round: { color: 'subtle', variant: 'round' },
    secondary: { color: 'default', variant: 'outline' },
    subtle: { color: 'subtle', variant: 'solid' },
};

const LEGACY_VARIANTS = Object.keys(LEGACY_VARIANT_TO_PRESENTATION) as LegacyButtonVariant[];

const isLegacyVariant = (variant: ButtonOwnProps['variant']): variant is LegacyButtonVariant =>
    variant != null && LEGACY_VARIANTS.includes(variant as LegacyButtonVariant);

const resolvePresentation = (
    variant: ButtonOwnProps['variant'],
    color: ButtonOwnProps['color'],
): { color: ButtonColor; variant: ButtonVariant } => {
    if (variant == null) {
        return { color: color ?? 'default', variant: 'solid' };
    }

    if (isLegacyVariant(variant)) {
        const legacyPresentation = LEGACY_VARIANT_TO_PRESENTATION[variant];
        return { color: color ?? legacyPresentation.color, variant: legacyPresentation.variant };
    }

    return { color: color ?? 'default', variant };
};

const getVariantClasses = (
    variant: ButtonVariant,
    color: ButtonColor,
    disabled: boolean,
    disabledVariant: ButtonProps['disabledVariant'],
    isActive: boolean,
    iconOnly: boolean,
    size: ButtonProps['size'],
): string => {
    switch (variant) {
        case 'solid':
            return getSolidVariantClasses(color, disabled, disabledVariant, isActive);
        case 'outline':
            return getOutlineVariantClasses(disabled, isActive);
        case 'ghost':
            return getGhostVariantClasses(disabled, isActive);
        case 'link':
            return getLinkVariantClasses(disabled);
        case 'round':
            return getRoundVariantClasses(color, disabled, iconOnly, size);
    }
};

const baseClasses = clsx(
    'inline-flex items-center justify-center',
    'gap-s border-none rounded-lg outline-none',
    'font-sans font-medium no-underline',
    'transition-all duration-200 ease-in-out',
    '[&>svg]:h-4 [&>svg]:w-4',
    'focus-visible:outline focus-visible:outline-1 focus-visible:outline-orange-800',
);

export const Button = <E extends ElementType = 'button'>({
    as,
    children,
    className = '',
    color,
    disabled = false,
    disabledVariant,
    iconOnly = false,
    isActive = false,
    size,
    type = 'button',
    variant = 'solid',
    ...props
}: ButtonProps<E>): ReactElement => {
    const Component = as ?? 'button';
    const resolvedPresentation = resolvePresentation(variant, color);

    return (
        <Component
            className={clsx(
                baseClasses,
                getSizeClasses(size, iconOnly),
                getVariantClasses(
                    resolvedPresentation.variant,
                    resolvedPresentation.color,
                    disabled,
                    disabledVariant,
                    isActive,
                    iconOnly,
                    size,
                ),
                disabled ? 'opacity-50 cursor-default' : 'cursor-pointer',
                !disabled && 'hover:opacity-80',
                className,
            )}
            disabled={disabled}
            type={Component === 'button' ? type : undefined}
            {...props}
        >
            {children}
        </Component>
    );
};
